import React from 'react'
import useThrottle from 'react-use/lib/useThrottle'
import { getChannelLabel } from 'data/channels/helpers'
import Entry from '../SearchEntryRecord'
import searchOnAlgolia from './searchOnAlgolia'
import useSuspendedLoadingState from '../useSuspendedLoadingState'
import Fuse from 'fuse.js'
import uniq from 'lodash/uniq'

const RECENT_SEARCHES_COUNT = 1
const RECENT_NOTES_COUNT = 7
const RECENT_CHANNELS_COUNT = 3
const SWITCH_TO_FULL_TEXT_SEARCH_ROW_INDEX = 2

function createChannelEntry(hit) {
  return Entry({
    key: `channel-${hit.id}`,
    type: 'channels',
    data: hit,
    searchToken: getChannelLabel(hit),
  })
}

function createNoteEntry(hit) {
  return Entry({
    key: `note-${hit.id}`,
    type: 'notes',
    data: hit,
    searchToken: hit.title,
    channelId: hit.channelId,
  })
}

function createSearchEntry(term) {
  return Entry({
    key: `search-${term}`,
    type: 'search',
    data: term,
    searchToken: term,
  })
}

function getXUniqueEntries(arr, numberOfEntries) {
  return Array.from(arr).slice(0, numberOfEntries)
}

function getLastVisitedNotes(allNotes, lastVisitedNotesIds) {
  return getXUniqueEntries(lastVisitedNotesIds, RECENT_NOTES_COUNT).map(id => {
    return allNotes.find(note => note.id === id)
  })
}

function getLastVisitedChannels(allChannels, lastVisitedChannelsIds) {
  return getXUniqueEntries(lastVisitedChannelsIds, RECENT_CHANNELS_COUNT).map(
    id => allChannels.find(channel => channel && channel.id === id)
  )
}

export default function useQuickSwitchResults({
  query,
  algoliaClient,
  allNotes,
  lastVisitedNotesIds,
  allChannels,
  lastVisitedChannelsIds,
  lastSearchedTerms,
  organization,
  activeNoteId,
}) {
  const [results, setResults] = React.useState([])
  const [isLoading, setIsLoading] = useSuspendedLoadingState(false)
  const queryRef = React.useRef()
  queryRef.current = query
  const throttledQuery = useThrottle(query, 200)
  React.useEffect(
    () => {
      const throttledSearch = async () => {
        let results
        if (query) {
          setIsLoading(true)

          const { notes, channels } = await searchOnAlgolia({
            query: throttledQuery,
            client: algoliaClient,
            organization,
          })

          const fuse = new Fuse(
            lastSearchedTerms.map(term => ({ term })).toArray(),
            {
              shouldSort: true,
              threshold: 0.3,
              location: 0,
              distance: 100,
              maxPatternLength: 32,
              minMatchCharLength: 1,
              keys: ['term'],
            }
          )

          const matchingSearchedTerms = fuse.search(query)

          const searchedTerms = uniq([
            ...matchingSearchedTerms
              .map(({ term }) => term)
              .filter(term => term !== query),
          ])

          const { recentNotes, nonRecentNotes } = notes.reduce(
            ({ recentNotes, nonRecentNotes }, note) => {
              if (lastVisitedNotesIds.includes(note.id)) {
                return {
                  nonRecentNotes,
                  recentNotes: [...recentNotes, note],
                }
              }
              return {
                recentNotes,
                nonRecentNotes: [...nonRecentNotes, note],
              }
            },
            { recentNotes: [], nonRecentNotes: [] }
          )

          const { recentChannels, nonRecentChannels } = channels.reduce(
            ({ recentChannels, nonRecentChannels }, channel) => {
              if (lastVisitedChannelsIds.includes(channel.id)) {
                return {
                  nonRecentChannels,
                  recentChannels: [...recentChannels, channel],
                }
              }
              return {
                recentChannels,
                nonRecentChannels: [...nonRecentChannels, channel],
              }
            },
            { recentChannels: [], nonRecentChannels: [] }
          )

          results = [
            ...recentNotes.map(createNoteEntry),
            ...recentChannels.map(createChannelEntry),
            ...nonRecentChannels.map(createChannelEntry),
            ...searchedTerms.map(createSearchEntry),
            ...nonRecentNotes.map(createNoteEntry),
          ].filter(result => Boolean(result.searchToken))

          results.splice(
            SWITCH_TO_FULL_TEXT_SEARCH_ROW_INDEX,
            0,
            createSearchEntry(query)
          )
        } else {
          const lastVisitedNotes = getLastVisitedNotes(
            allNotes,
            lastVisitedNotesIds.filter(noteId => activeNoteId !== noteId)
          )
          const lastVisitedNotesEntries = lastVisitedNotes.map(
            note => (note ? createNoteEntry(note.toJS()) : null)
          )
          const lastVisitedChannels = getLastVisitedChannels(
            allChannels,
            lastVisitedChannelsIds
          )
          const lastVisitedChannelsEntries = lastVisitedChannels.map(
            channel => (channel ? createChannelEntry(channel.toJS()) : null)
          )

          const lastSearchedTermEntries = lastSearchedTerms
            .slice(0, RECENT_SEARCHES_COUNT)
            .map(createSearchEntry)

          results = [
            ...lastSearchedTermEntries,
            ...lastVisitedNotesEntries,
            ...lastVisitedChannelsEntries,
          ].filter(x => x)
        }

        // discard results if query has changed
        if (queryRef.current !== query) {
          return
        }
        setIsLoading(false)
        setResults(results)
      }
      throttledSearch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      throttledQuery,
      allNotes,
      lastVisitedNotesIds,
      allChannels,
      lastVisitedChannelsIds,
      activeNoteId,
    ]
  )

  return [results, isLoading]
}
